/**
 * In-app volume settings (MUS-87): each sound channel has its own level and
 * its own mute, and no control acts on every channel at once.
 *
 * The device volume is the master, so there is no master level here. The
 * channel names are the sound layer's names for these sources, so the later
 * settings model can adopt saved values without renaming them.
 */

export const VOLUME_CHANNELS = ['piano', 'metronome', 'narration', 'sfx'] as const;

export type VolumeChannel = (typeof VOLUME_CHANNELS)[number];

export interface ChannelVolume {
  /** The channel's level, a whole percent from 0 to 100. */
  level: number;
  /** Silences this channel only, without changing its level. */
  muted: boolean;
}

export type VolumeSettings = Record<VolumeChannel, ChannelVolume>;

export const MIN_VOLUME = 0;
export const MAX_VOLUME = 100;

export const VOLUME_CHANNEL_LABELS: Record<VolumeChannel, string> = {
  piano: 'Piano',
  metronome: 'Metronome',
  narration: 'Narration',
  sfx: 'Sound effects',
};

const FULL_UNMUTED: ChannelVolume = { level: MAX_VOLUME, muted: false };

/** What a user with no saved settings gets: every channel at full, unmuted. */
export const DEFAULT_VOLUME_SETTINGS: VolumeSettings = {
  piano: FULL_UNMUTED,
  metronome: FULL_UNMUTED,
  narration: FULL_UNMUTED,
  sfx: FULL_UNMUTED,
};

export const isVolumeChannel = (value: unknown): value is VolumeChannel =>
  typeof value === 'string' && VOLUME_CHANNELS.includes(value as VolumeChannel);

/** A whole percent from 0 to 100, or null when the value is not a number. */
export const clampVolume = (value: unknown): number | null => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, Math.round(value)));
};

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

/**
 * One channel from a stored record. The current shape keeps `{ level, muted }`
 * under the channel's name. The first web build's shape kept the levels under
 * `levels` with one `muted` over every channel: its levels carry across and
 * that mute is dropped, so every channel reads unmuted.
 */
const readStoredChannel = (
  record: Record<string, unknown>,
  channel: VolumeChannel,
): ChannelVolume => {
  const stored = asRecord(record[channel]);
  const earlierLevel = asRecord(record.levels)[channel];
  const level =
    clampVolume(stored.level) ??
    clampVolume(earlierLevel) ??
    DEFAULT_VOLUME_SETTINGS[channel].level;
  const muted =
    typeof stored.muted === 'boolean' ? stored.muted : DEFAULT_VOLUME_SETTINGS[channel].muted;
  return { level, muted };
};

/**
 * Settings from anything read back from storage. Missing or unreadable fields
 * fall back to the defaults one by one, so a partial or older record keeps
 * whatever it does carry.
 */
export const normalizeVolumeSettings = (raw: unknown): VolumeSettings => {
  const record = asRecord(raw);
  return VOLUME_CHANNELS.reduce<VolumeSettings>(
    (result, channel) => ({ ...result, [channel]: readStoredChannel(record, channel) }),
    { ...DEFAULT_VOLUME_SETTINGS },
  );
};

/**
 * The output gain (0 to 1) for a level. Loudness is heard on a log scale, so a
 * straight percent-to-amplitude map crowds every audible change into the top
 * of the slider; squaring it spreads the change across the whole slider.
 */
export const volumeToGain = (volume: number): number => {
  const level = clampVolume(volume) ?? MAX_VOLUME;
  return (level / MAX_VOLUME) ** 2;
};

/** The gain a channel plays at: zero while it is muted, otherwise its level's gain. */
export const channelGain = (settings: VolumeSettings, channel: VolumeChannel): number =>
  settings[channel].muted ? 0 : volumeToGain(settings[channel].level);

export const areVolumeSettingsEqual = (a: VolumeSettings, b: VolumeSettings): boolean =>
  VOLUME_CHANNELS.every(
    (channel) => a[channel].level === b[channel].level && a[channel].muted === b[channel].muted,
  );
